import React, { useCallback } from 'react';
import { usePostPayin } from 'api/hooks/usePostPayin';
import type { IPayin, IPayinObject } from 'api/resources/payin';
import {
	PaymentsModal as PaymentModalComponent,
	EPaymentsFormStatus,
} from 'app/features/Payments/PaymentsModal';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { type TPage } from '../Analytics/utils/constants';

interface IUsePayments {
	onClose?: () => void;
	onSuccess?: () => void;
}

export const usePayments = (page: TPage, props?: IUsePayments) => {
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token') || '';
	const [isOpen, setIsOpen] = useState(false);
	const { mutateAsync: mutatePayin } = usePostPayin();
	const [payinInfo, setPayinInfo] = useState<null | IPayin>(null);
	const [paymentsFormStatus, setPaymentsFormStatus] = useState(
		EPaymentsFormStatus.Idle,
	);
	const [payinItems, setPayinItems] = useState<IPayinObject[]>([]);

	const loadPaymentModal = useCallback(
		async (paymentItems: IPayinObject[]) => {
			setPaymentsFormStatus(EPaymentsFormStatus.Loading);
			setPayinItems(paymentItems);

			const response = await mutatePayin({ token, payload: paymentItems });

			setPayinInfo(response.data);
			setIsOpen(true);
			setPaymentsFormStatus(EPaymentsFormStatus.Idle);
		},
		[mutatePayin, token],
	);

	const onClose = () => {
		props?.onClose?.();
		setIsOpen(!isOpen);
	};

	// Do not render modal till payinInfo is available
	// to minimize the gymnastics of conditionally rendering
	// the non-React Rainforest widget and setting its on events
	const paymentsModal =
		// check for isOpen to trigger recalculation of the subtotal, fees & total
		payinInfo && isOpen ? (
			<PaymentModalComponent
				payinInfo={payinInfo}
				isOpen={isOpen}
				onSuccess={props?.onSuccess}
				onClose={onClose}
				paymentsFormStatus={paymentsFormStatus}
				onStatusChange={setPaymentsFormStatus}
				payinItems={payinItems}
				page={page}
			/>
		) : null;

	return {
		loadPaymentModal,
		paymentsModal,
		paymentsFormStatus,
	};
};
